import { useCallback, useLayoutEffect, useRef } from 'react';

// Never scroll the document to reveal a field. First fit the input to the space
// left by wrapped album metadata, then scroll the dialog only when necessary.
export function revealDialogField(root) {
  const active = document.activeElement;
  if (
    !root?.contains(active) ||
    active?.tagName !== 'TEXTAREA' ||
    root.dataset.entered !== 'true' ||
    root.dataset.viewportResizing === 'true'
  )
    return;
  const body = active.closest('.MuiDialogContent-root');
  const field = active.closest('.pp-album-review-field');
  if (!body || !field || !body.clientHeight) return;
  const frame = body.getBoundingClientRect();
  const shell = active.closest('.pp-review-input-shell');
  if (shell && root.dataset.keyboardOpen === 'true' && window.innerWidth < 768) {
    const available = frame.bottom - shell.getBoundingClientRect().top - body.scrollTop - 12;
    shell.style.height = `${Math.max(80, Math.min(180, available))}px`;
  } else shell?.style.removeProperty('height');
  const box = field.getBoundingClientRect();
  const bottom = box.bottom - frame.bottom + 12;
  const top = box.top - frame.top - 12;
  if (bottom > 2) body.scrollTop += bottom;
  else if (top < -2) body.scrollTop += top;
}

// Focus must stay synchronous with the tap to open the iOS keyboard. Its native
// caret must not be painted while an ancestor is sliding or the viewport is
// resizing: WebKit updates selection geometry independently of that animation.
export default function useDialogViewport() {
  const root = useRef(null);
  const controller = useRef({});
  const onEntered = useCallback(() => {
    if (root.current) root.current.dataset.entered = 'true';
    controller.current.settle?.(true);
  }, []);
  const onFieldFocus = useCallback(() => controller.current.focus?.(), []);
  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    const events = viewport || window;
    let frame, paintFrame, settleTimer;
    let epoch = 0;
    let focusUntil = 0;
    let geometryUntil = 0;
    let focusReveal = false;
    let resizeReveal = false;
    let fullHeight = Math.max(window.innerHeight, viewport?.height || 0);
    let width = window.innerWidth;
    const hideCaret = () => {
      if (root.current) root.current.dataset.caretReady = 'false';
      epoch++;
      cancelAnimationFrame(paintFrame);
    };
    const measure = () => {
      const node = root.current;
      if (!node || (viewport?.scale || 1) !== 1) return;
      const height = viewport?.height || window.innerHeight;
      if (width !== window.innerWidth) {
        width = window.innerWidth;
        fullHeight = Math.max(window.innerHeight, height);
      }
      fullHeight = Math.max(fullHeight, height);
      const keyboard = fullHeight - height > 120;
      node.dataset.keyboardOpen = String(keyboard);
      node.style.setProperty('--dialog-viewport-height', `${height}px`);
      node.style.setProperty(
        '--dialog-viewport-top',
        `${keyboard ? Math.max(0, viewport?.offsetTop || 0) : 0}px`,
      );
      node.dataset.compactReview = String(height < 460 && width < 768);
      if (!keyboard) node.querySelector('.pp-review-input-shell')?.style.removeProperty('height');
    };
    const settle = (fromFocus = false) => {
      focusReveal ||= fromFocus;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(
        () => {
          const node = root.current;
          if (!node) return;
          node.dataset.viewportResizing = 'false';
          if (node.dataset.entered !== 'true') return; // onEntered will resume.
          measure();
          if (focusReveal || (resizeReveal && node.dataset.keyboardOpen === 'true'))
            revealDialogField(node);
          focusReveal = false;
          resizeReveal = false;
          // Paint the final field geometry before exposing the native caret. No
          // blur/refocus or selection rewrite: keyboard, draft and IME stay intact.
          const ticket = epoch;
          paintFrame = requestAnimationFrame(() => {
            paintFrame = requestAnimationFrame(() => {
              if (ticket === epoch && root.current === node) node.dataset.caretReady = 'true';
            });
          });
        },
        Math.max(0, focusUntil - Date.now(), geometryUntil - Date.now()),
      );
    };
    controller.current = {
      settle,
      focus: () => {
        hideCaret();
        // A tap can precede Safari's first keyboard resize event.
        focusUntil = Date.now() + (window.innerWidth < 1280 ? 350 : 0);
        settle(true);
      },
    };
    const schedule = (event) => {
      if (event.type === 'resize') {
        resizeReveal = true;
        hideCaret();
        if (root.current) root.current.dataset.viewportResizing = 'true';
        geometryUntil = Date.now() + 120;
        settle();
      } else if (root.current?.dataset.caretReady === 'false') {
        // Initial keyboard panning may finish after its resize event. Once
        // stable, ordinary user scrolling does not hide the caret or auto-scroll.
        geometryUntil = Date.now() + 120;
        settle();
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    events.addEventListener('resize', schedule);
    events.addEventListener('scroll', schedule);
    return () => {
      epoch++;
      controller.current = {};
      cancelAnimationFrame(frame);
      cancelAnimationFrame(paintFrame);
      clearTimeout(settleTimer);
      events.removeEventListener('resize', schedule);
      events.removeEventListener('scroll', schedule);
    };
  }, []);
  return { ref: root, onEntered, onFieldFocus };
}
